import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import useBundlesGrouped from "@/features/bundles/hooks/use-bundles-grouped";
import { codeToFlag } from "@/features/regions/utils/code-to-flag";
import { GroupedBundleLeaf } from "@/pages/bundles/types/grouped-bundle";

type Props = {
  value?: string | null; // selected standard_id
  onSelect: (bundle: GroupedBundleLeaf) => void;
};

// Searchable dropdown of ACTIVE offerings (one current bundle per standard_id),
// reusing the grouped bundle endpoint and flattening to each offering's current
// bundle. Region-aware: only shows offerings in the active global region.
export default function OfferingCombobox({ value, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { data, isLoading } = useBundlesGrouped({});

  const activeBundles = useMemo(() => {
    return (data?.groups ?? [])
      .flatMap((g) => g.offerings)
      .map((o) => o.current)
      .filter((b): b is GroupedBundleLeaf => Boolean(b));
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeBundles;
    return activeBundles.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        b.standard_id.toLowerCase().includes(q),
    );
  }, [activeBundles, query]);

  const selected = activeBundles.find((b) => b.standard_id === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between rounded-md border border-input px-3 py-2 text-sm"
      >
        {selected ? (
          <span className="flex items-center gap-2 min-w-0">
            <span>{codeToFlag(selected.region_rel?.code)}</span>
            <span className="truncate">{selected.name}</span>
            <span className="text-gray-400 whitespace-nowrap">
              · {selected.currency} {selected.amount.toLocaleString()}
            </span>
          </span>
        ) : (
          <span className="text-gray-400">Search active offerings…</span>
        )}
        <ChevronsUpDown className="h-4 w-4 text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or standard_id…"
              className="w-full text-sm outline-none"
            />
          </div>
          <div className="max-h-64 overflow-auto py-1">
            {isLoading && (
              <div className="px-3 py-2 text-sm text-gray-400">Loading…</div>
            )}
            {!isLoading && filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">
                No active offerings
              </div>
            )}
            {filtered.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => {
                  onSelect(b);
                  setOpen(false);
                  setQuery("");
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <span>{codeToFlag(b.region_rel?.code)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block truncate">{b.name}</span>
                  <span className="block truncate text-[11px] font-mono text-gray-400">
                    {b.standard_id}
                  </span>
                </span>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {b.currency} {b.amount.toLocaleString()}
                </span>
                {value === b.standard_id && (
                  <Check className="h-4 w-4 text-green-600" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
